import { t } from '@/features/i18n/utils';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useAuth } from '@/features/auth';
import { Header, HeaderBackButton, HeaderButton, HeaderCopyButton } from '@/ui/components/header';
import { Breadcrumb, BreadcrumbPage } from '@/ui/components/breadcrumb';
import { Icon } from '@/ui/components/icon';
import { FileBookmarkIcon, PlusSignIcon } from '@hugeicons-pro/core-stroke-rounded';
import { MainScrollArea, MainHeader } from '@/ui/components/main';
import { Button } from '@/ui/components/button';
import { H1 } from '@/ui/components/heading';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Tabs, TabsList, TabsPanel, TabsTab } from '@/ui/components/tabs';
import { CreateChecklistTemplate } from '@/features/checklists/components/create-checklist-template';
import { kgChecklistTemplatesQuery } from '@/features/work-packages/queries';
import { WorkPackageTemplatesList } from '@/features/work-packages/components/work-package-templates-list';
import { getAllTemplateLists } from '@/features/folders/utils';
import { ExtractionTemplatesList } from '@/features/extractions/components/extraction-templates-list';
import { ContractReviewTemplatesList } from '@/features/contract-review/components/contract-review-templates-list';
import { ContractReviewTemplateDialog } from '@/features/contract-review/components/contract-review-template-dialog';
import { contractReviewTemplatesQuery } from '@/features/contract-review/queries';
import { useLocale } from 'use-intl';
import { folderTreeQuery } from '@/features/folders/queries';
import { useQuery } from '@tanstack/react-query';
import { createContractReviewTemplateOpenAtom } from '@/features/contract-review/store';
import {
  createChecklistTemplateOpenAtom,
  createWpTemplateOpenAtom,
  editChecklistTemplateAtom,
  templatesTabAtom,
} from '@/features/checklists/store';
import { ChecklistTemplateFormDialog } from '@/features/checklists/components/checklist-template-form-dialog';
import { checklistTemplatesQuery } from '@/features/checklists/queries';
import { ChecklistTemplatesList } from '@/features/checklists/components/checklist-templates-list';

export const Route = createFileRoute('/_authed/settings/templates/')({
  component: RouteComponent,
});

function RouteComponent() {
  const auth = useAuth();
  const organizationId = auth.user.organizationId;
  const { data: tree } = useQuery(folderTreeQuery(organizationId));
  const hasData = tree && (tree.folders.length > 0 || tree.templateLists.length > 0);

  const tab = useAtomValue(templatesTabAtom);
  const openCreateWpTemplate = useSetAtom(createWpTemplateOpenAtom);
  const openCreateContractReviewTemplate = useSetAtom(createContractReviewTemplateOpenAtom);
  const openCreateChecklistTemplate = useSetAtom(createChecklistTemplateOpenAtom);
  const setEditChecklistTemplate = useSetAtom(editChecklistTemplateAtom);
  const isExtractionTab = tab === 'extraction_templates';

  const openCreateForTab = () => {
    if (tab === 'wp_templates') return openCreateWpTemplate(true);
    if (tab === 'checklist_templates') {
      setEditChecklistTemplate(null);
      return openCreateChecklistTemplate(true);
    }
    openCreateContractReviewTemplate(true);
  };

  return (
    <>
      <Header>
        <HeaderBackButton to="/" />
        <Breadcrumb>
          <BreadcrumbPage>
            <Icon icon={FileBookmarkIcon} />
            {t({ en: 'Templates', nb: 'Maler' })}
          </BreadcrumbPage>
        </Breadcrumb>
        {isExtractionTab ? (
          <HeaderButton
            nativeButton={false}
            aria-label={t({ en: 'New template', nb: 'Ny mal' })}
            render={
              <Link to="/settings/templates/new">
                <Icon icon={PlusSignIcon} />
              </Link>
            }
          />
        ) : (
          <HeaderButton
            aria-label={t({ en: 'New template', nb: 'Ny mal' })}
            onClick={openCreateForTab}
          >
            <Icon icon={PlusSignIcon} />
          </HeaderButton>
        )}
        <HeaderCopyButton />
      </Header>
      <MainScrollArea>
        <MainHeader className="items-start">
          <div className="flex flex-col items-start gap-2">
            <H1>{t({ en: 'Templates', nb: 'Maler' })}</H1>
            <p className="text-muted text-sm">
              {t({
                en: 'Reusable setups for consistent, repeated actions across every project.',
                nb: 'Gjenbrukbare oppsett for konsistente, gjentatte handlinger på tvers av alle prosjekter.',
              })}
            </p>
          </div>
          {isExtractionTab ? (
            <Button
              className="max-md:w-full"
              kind="with-icon"
              variant={!hasData ? 'secondary' : 'primary'}
              nativeButton={false}
              render={
                <Link to="/settings/templates/new">
                  <Icon icon={PlusSignIcon} />
                  {t({ en: 'New template', nb: 'Ny mal' })}
                </Link>
              }
            />
          ) : (
            <Button
              className="max-md:w-full"
              kind="with-icon"
              variant={!hasData ? 'secondary' : 'primary'}
              onClick={openCreateForTab}
            >
              <Icon icon={PlusSignIcon} />
              {t({ en: 'New template', nb: 'Ny mal' })}
            </Button>
          )}
        </MainHeader>
        <Content />
      </MainScrollArea>
    </>
  );
}

const TABS = [
  'extraction_templates',
  'checklist_templates',
  'wp_templates',
  'legal_deviations_templates',
] as const;

function Content() {
  const locale = useLocale();
  const auth = useAuth();
  const organizationId = auth.user.organizationId;
  const [tab, setTab] = useAtom(templatesTabAtom);
  const [contractReviewTemplateOpen, setContractReviewTemplateOpen] = useAtom(
    createContractReviewTemplateOpenAtom,
  );

  const { data: tree } = useQuery(folderTreeQuery(organizationId));
  const { data: checklistTemplates = [] } = useQuery(
    checklistTemplatesQuery(organizationId, locale),
  );
  const { data: wpTemplates = [] } = useQuery(kgChecklistTemplatesQuery(organizationId));
  const { data: contractReviewTemplates } = useQuery(contractReviewTemplatesQuery(organizationId));

  const extractionTemplateCount = tree ? getAllTemplateLists(tree).length : 0;

  return (
    <Tabs value={tab} onValueChange={setTab} className="flex flex-col grow min-h-0">
      <TabsList className={'px-x h-10'}>
        <TabsTab value={TABS[0]}>
          {t({ en: 'Extractions', nb: 'Uttrekk' })} ({extractionTemplateCount})
        </TabsTab>
        <TabsTab value={TABS[1]}>
          {t({ en: 'Checklists', nb: 'Sjekklister' })} ({checklistTemplates.length})
        </TabsTab>
        <TabsTab value={TABS[2]}>
          {t({ en: 'Work package checklists', nb: 'Sjekklister for arbeidspakker' })} (
          {wpTemplates.length})
        </TabsTab>
        <TabsTab value={TABS[3]}>
          {t({ en: 'Contract review', nb: 'Kontraktsgjennomgang' })} (
          {contractReviewTemplates?.length ?? 0})
        </TabsTab>
      </TabsList>
      <TabsPanel value={TABS[0]}>
        <ExtractionTemplatesList />
      </TabsPanel>
      <TabsPanel value={TABS[1]}>
        <ChecklistTemplatesList />
      </TabsPanel>
      <TabsPanel value={TABS[2]}>
        <WorkPackageTemplatesList />
      </TabsPanel>
      <TabsPanel value={TABS[3]}>
        <ContractReviewTemplatesList />
      </TabsPanel>
      <CreateChecklistTemplate />
      <ChecklistTemplateFormDialog />
      <ContractReviewTemplateDialog
        open={contractReviewTemplateOpen}
        onOpenChange={setContractReviewTemplateOpen}
      />
    </Tabs>
  );
}
